import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '麻花影院',
    url: 'https://mahua.tv',
    description: '麻花影院是一家专业的免费在线影视播放平台，致力于为全球华语用户提供海量高清电影、电视剧、综艺、动漫、纪录片等免费在线播放服务。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'kefu@mahua.tv',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于麻花影院',
    description: '了解麻花影院的品牌故事、核心优势和联系方式',
    url: 'https://mahua.tv/about',
  };

  const stats = [
    { number: '10000+', label: '影视资源' },
    { number: '500万+', label: '日均用户' },
    { number: '99.9%', label: '服务可用率' },
    { number: '24/7', label: '内容更新' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <main className="min-h-screen pt-20 pb-12">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <section className="text-center mb-16">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                关于麻花影院
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                麻花影院是一家专业的免费在线影视播放平台，致力于为全球华语用户提供海量高清电影、电视剧、综艺、动漫、纪录片等免费在线播放服务。高清画质，流畅不卡顿。
              </p>
            </section>

            <section className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
              {stats.map((stat, index) => (
                <div key={index} className="text-center p-6 rounded-xl bg-background-100">
                  <p className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-2">
                    {stat.number}
                  </p>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </section>

            <section className="mb-16">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-8">
                品牌故事
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="p-6 rounded-xl bg-background-100">
                  <h3 className="font-heading font-semibold text-foreground-950 mb-3">我们的使命</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    麻花影院诞生于对影视艺术的热爱。我们相信，好的影视作品应该被更多人看到，而不应被付费墙阻隔。我们的使命是打造一个免费、高清、流畅的在线观影平台，让每一位用户都能轻松享受高质量的影视内容。从热门大片到经典老片，从国产剧到海外佳作，麻花影院努力覆盖最全面的影视资源库。
                  </p>
                </div>
                <div className="p-6 rounded-xl bg-background-100">
                  <h3 className="font-heading font-semibold text-foreground-950 mb-3">技术驱动体验</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们采用全球领先的CDN加速技术和智能视频编码方案，确保用户无论身在何处，都能享受秒开不卡顿的观影体验。支持多清晰度自适应切换，在保证画质的同时优化带宽使用。同时，我们的推荐算法能够精准分析用户喜好，为您推送最感兴趣的影视内容，让发现好片变得更简单。
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-8">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    name: '影迷小王',
                    text: '用了麻花影院半年了，资源真的很全，想看的电影基本都能找到。画质清晰，加载也快，推荐给身边好多朋友了！',
                    stars: 5,
                  },
                  {
                    name: '追剧达人',
                    text: '追剧必备！更新速度超快，热门剧集同步更新。关键是免费还没有乱七八糟的广告弹窗，体验真的很好。',
                    stars: 5,
                  },
                  {
                    name: '动漫爱好者',
                    text: '国漫资源很齐全，斗破苍穹、完美世界都在追。画质清晰流畅，周末窝在家一口气能看好几集，太爽了！',
                    stars: 4,
                  },
                ].map((review, index) => (
                  <div key={index} className="p-6 rounded-xl bg-background-100">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < review.stars ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      「{review.text}」
                    </p>
                    <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-8">
                联系我们
              </h2>
              <div className="max-w-lg mx-auto p-6 rounded-xl bg-background-100">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground-950 text-sm">客服邮箱</h4>
                      <p className="text-sm text-foreground-600 mt-0.5">kefu@mahua.tv</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground-950 text-sm">工作时间</h4>
                      <p className="text-sm text-foreground-600 mt-0.5">周一至周日 9:00 - 22:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-information-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground-950 text-sm">版权声明</h4>
                      <p className="text-sm text-foreground-600 mt-0.5">
                        本站所有内容均来自网络共享资源，仅供交流学习使用。如果侵犯了您的权益，请通过邮箱联系我们，我们将及时处理。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}